import { type ClientError } from "graphql-request"

import {
  createAccordionOpenEvent,
  createCardClickedEvent,
  createComponentClickedEvent,
  createComponentViewedEvent,
  createErrorEvent,
  createListScrolledEvent,
  createScreenViewedEvent,
  createSubmittedEvent,
  track,
} from "./base"
import {
  type CombinedLoanAndProductsQuery
} from "@/generated/graphql"
import { type AppScreen } from "@/constants/Screens"

export const loansViewed = ({
  currentTab,
  hasLoans,
}: {
  currentTab: string
  hasLoans: boolean
}) => {
  track(
    createScreenViewedEvent({
      current_screen: "Loans",
      current_tab: currentTab,
      screen_state: hasLoans ? "has-loans" : "no-loans",
    })
  )
}

export const loanListViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "Loan List",
    })
  )
}

export const loanPerkViewed = () => {
  track(
    createComponentViewedEvent({
      current_screen: "Loan Confirm",
      component_type: "banner",
      component_function: "perk alert",
      component_size: "small",
      component_position: "middle",
      component_text:
        "BACKFLIP PERK ALERT!\nThis deal may qualify for special rates.*",
    })
  )
}

export const loanProductsQuestionClicked = (screenName: AppScreen) => {
  track(
    createComponentClickedEvent({
      current_screen: screenName,
      component_type: "text_link",
      component_function: "navigate to question url",
      component_text: "Questions? Contact a lending expert",
    })
  )
}

export const loanConfirmViewed = ({
  propertyAddress,
  arv,
  purchasePrice,
  rehabCost,
  earnestMoneyDeposit,
  rewardsCredit,
  cashToClose,
  leverage,
  loanAmount,
  interestRate,
  moPayment,
  financingFees,
  origFee,
  borrower,
  flips,
  rentals,
  cash,
  creditScore,
  contractStatus,
  closingDate,
}: {
  propertyAddress: string
  arv?: number | null
  purchasePrice?: number | null
  rehabCost?: number | null
  earnestMoneyDeposit?: number | null
  rewardsCredit?: number | null
  cashToClose?: number | null
  leverage?: number | null
  loanAmount?: number | null
  interestRate?: number | null
  moPayment?: number | null
  financingFees?: number | null
  origFee?: number | null
  borrower?: string | null
  flips?: number | null
  rentals?: number | null
  cash?: string | null
  creditScore?: string | null
  contractStatus?: string | null
  closingDate?: string | null
}) => {
  track(
    createScreenViewedEvent({
      current_screen: "Loan Confirm",
      property_address: propertyAddress,
      viewed_values: {
        arv,
        purchase_price: purchasePrice,
        rehab_cost: rehabCost,
        earnest_money_deposit: earnestMoneyDeposit,
        rewards_credit: rewardsCredit,
        cash_to_close: cashToClose,
        leverage,
        loan_amount: loanAmount,
        interest_rate: interestRate,
        mo_payment: moPayment,
        financing_fees: financingFees,
        orig_fee: origFee,
        borrower,
        flips,
        rentals,
        cash,
        credit_score: creditScore,
        contract_status: contractStatus,
        closing_date: closingDate,
      },
    })
  )
}

export const loanApplicationSubmittedSuccessfullyViewed = () => {
  track(
    createScreenViewedEvent({
      current_screen: "Loan App Submitted Successfully",
    })
  )
}

export const loanApplicationSubmitted = ({
  propertyAddress,
  arv,
  purchasePrice,
  rehabCost,
  earnestMoneyDeposit,
  rewardsCredit,
  cashToClose,
  leverage,
  loanAmount,
  interestRate,
  moPayment,
  financingFees,
  origFee,
  borrower,
  flips,
  rentals,
  cash,
  creditScore,
  contractStatus,
  closingDate,
}: {
  propertyAddress: string
  arv?: number | null
  purchasePrice?: number | null
  rehabCost?: number | null
  earnestMoneyDeposit?: number | null
  rewardsCredit?: number | null
  cashToClose?: number | null
  leverage?: number | null
  loanAmount?: number | null
  interestRate?: number | null
  moPayment?: number | null
  financingFees?: number | null
  origFee?: number | null
  borrower?: string | null
  flips?: number | null
  rentals?: number | null
  cash?: string | null
  creditScore?: string | null
  contractStatus?: string | null
  closingDate?: string | null
}) => {
  track(
    createSubmittedEvent({
      object_name: "Loan Application",
      current_screen: "Loan Confirm",
      to_screen: "Loan App Submitted Successfully",
      component_type: "button",
      component_text: "Submit a Non-Binding Application",
      submitted_values: {
        property_address: propertyAddress,
        arv,
        purchase_price: purchasePrice,
        rehab_cost: rehabCost,
        earnest_money_deposit: earnestMoneyDeposit,
        rewards_credit: rewardsCredit,
        cash_to_close: cashToClose,
        leverage,
        loan_amount: loanAmount,
        interest_rate: interestRate,
        mo_payment: moPayment,
        financing_fees: financingFees,
        orig_fee: origFee,
        borrower,
        flips,
        rentals,
        cash,
        credit_score: creditScore,
        contract_status: contractStatus,
        closing_date: closingDate,
      },
    })
  )
}

// Capital Products scrolled via Swipe vs. Button Press
export const capitalProductsListScrolled = ({
  scrolledTo,
  type,
}: {
  scrolledTo: number
  type: "click" | "swipe"
}) => {
  track(
    createListScrolledEvent({
      current_screen: "Loan Confirm",
      list_type: "loan product list",
      scrolled_to: scrolledTo,
      interaction_type: type,
    })
  )
}

export const setupScreenAccordionOpened = ({
  currentScreen,
  accordionKey,
  user = false,
}: {
  currentScreen: AppScreen
  accordionKey: string
  user?: boolean
}) => {
  track(
    createAccordionOpenEvent({
      current_screen: currentScreen,
      accordion_key: accordionKey,
      user,
    })
  )
}

export const capitalProductCardClicked = (
  cardIndex: number,
  productName: string | undefined,
  loan: NonNullable<CombinedLoanAndProductsQuery["loanApplication"]>
) => {
  track(
    createCardClickedEvent({
      current_screen: "Loan Confirm",
      to_screen: "Loan Confirm",
      card_type: "capital product",
      card_list_number: cardIndex,
      card_values: {
        loan_id: loan.id,
        productName,
      },
    })
  )
}

export const noProductYetClicked = () => {
  track(
    createComponentClickedEvent({
      current_screen: "Loan Setup - Property Info",
      component_function: "view loan products",
      component_type: "text_link",
      component_text: "No property yet?",
    })
  )
}

export const loanAppErrorEncountered = ({
  error,
  displayedMessage,
  propertyAddress,
  userName,
  userId,
}: {
  error: ClientError
  displayedMessage: string
  propertyAddress: string
  userName: string
  userId: string
}) => {
  track(
    createErrorEvent({
      event_name: "Loan Application Failed",
      message: `${error.message} \n
- Displays: ${displayedMessage} \n
- On Address: ${propertyAddress} \n
- User: ${userName} - (${userId})\n`,
    })
  )
}

export const loanDashboardAppInstallClicked = ({
  badgeType,
}: {
  badgeType: "apple_install_badge" | "google_install_badge"
}) => {
  track(
    createComponentClickedEvent({
      current_screen: "Loan Dashboard",
      component_type: badgeType,
      component_function: "link to app store",
    })
  )
}

export const loanSuccessAppInstallClicked = ({
  loan,
  badgeType,
}: {
  loan: NonNullable<CombinedLoanAndProductsQuery["loanApplication"]>
  badgeType: "apple_install_badge" | "google_install_badge"
}) => {
  const analysisSegmentProps = {
    analysis_id: loan.id,
    property_address: loan?.property.fullAddress,
  }

  track(
    createComponentClickedEvent({
      current_screen: "Loan App Submitted Successfully",
      component_type: badgeType,
      component_function: "link to app store",
      clicked_values: {
        ...analysisSegmentProps,
      },
    })
  )
}

export const loanDashboardEntryClicked = ({
  currentScreen,
  loanId,
  externalLoanId,
}: {
  currentScreen: AppScreen
  loanId?: string | null
  externalLoanId?: string | null
}) => {
  const clickedValues: Record<string, string> = {}
  if (loanId) {
    clickedValues.loan_id = loanId
  }
  if (externalLoanId) {
    clickedValues.external_loan_id = externalLoanId
  }

  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      component_type: "button",
      component_function: "open external dashboard",
      clicked_values: Object.keys(clickedValues).length > 0 ? clickedValues : undefined,
    })
  )
}

export const loanViewRatesClicked = ({
  currentScreen,
}: {
  currentScreen: AppScreen
}) => {
  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      component_type: "button",
      component_function: "view loan rates",
      component_text: "View rates",
    })
  )
}

export const loanGetQuoteClicked = ({
  currentScreen,
}: {
  currentScreen: AppScreen
}) => {
  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      component_type: "button",
      component_function: "start loan quote",
      component_text: "Get a quote",
    })
  )
}

export const loanCardClicked = ({
  currentScreen,
  loanId,
  externalLoanId,
  loanStatus,
  propertyAddress,
}: {
  currentScreen: AppScreen
  loanId: string
  externalLoanId?: string | null
  loanStatus: "draft" | "quote"
  propertyAddress?: string | null
}) => {
  const clickedValues: Record<string, string> = {
    loan_id: loanId,
    loan_status: loanStatus,
  }
  if (externalLoanId) {
    clickedValues.external_loan_id = externalLoanId
  }
  if (propertyAddress) {
    clickedValues.property_address = propertyAddress
  }

  track(
    createComponentClickedEvent({
      current_screen: currentScreen,
      component_type: "button",
      component_function: "open loan details",
      clicked_values: clickedValues,
    })
  )
}
